import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Users,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  CheckCircle2,
  Circle,
  Car,
  MapPin,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import TeacherDialog from "./TeacherDialog";

export default function TeachersTab() {
  const [teachers, setTeachers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // teacher obj or "new"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, v] = await Promise.all([
        api.get("/teachers", { params: { include_inactive: true } }),
        api.get("/vehicles").catch(() => ({ data: [] })),
      ]);
      setTeachers(Array.isArray(t.data) ? t.data : []);
      setVehicles(Array.isArray(v.data) ? v.data : []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const vehiclesById = useMemo(() => Object.fromEntries(vehicles.map((v) => [v.id, v])), [vehicles]);

  const toggleActive = async (t) => {
    try {
      await api.patch(`/teachers/${t.id}`, { active: !t.active });
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`¿Desactivar al profesor ${t.name}?`)) return;
    try {
      await api.delete(`/teachers/${t.id}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4" data-testid="teachers-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2"><Users className="h-5 w-5 text-orange-500" /> Profesores</h2>
          <p className="text-xs text-neutral-500 mt-0.5">Horarios, vacaciones, zona habitual y vehículo asignado.</p>
        </div>
        <button
          type="button"
          onClick={() => setEditing("new")}
          className="btn-primary"
          data-testid="teacher-add"
        >
          <Plus className="h-4 w-4" /> Nuevo profesor
        </button>
      </div>

      {loading ? (
        <div className="py-8 text-center text-sm text-neutral-500"><Loader2 className="inline h-4 w-4 animate-spin mr-2" />Cargando…</div>
      ) : teachers.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 p-8 text-center">
          <div className="text-sm text-neutral-600 font-semibold">No hay profesores registrados</div>
          <p className="text-xs text-neutral-500 mt-1">Añade el primer profesor para activar la planificación por profesor y vehículo.</p>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="min-w-full text-sm table-fixed">
            <colgroup>
              <col />
              <col style={{ width: "150px" }} />
              <col style={{ width: "160px" }} />
              <col style={{ width: "200px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "110px" }} />
            </colgroup>
            <thead className="text-[10px] uppercase tracking-wider text-neutral-500 bg-neutral-100">
              <tr>
                <th className="px-4 py-3 text-left">Nombre</th>
                <th className="px-4 py-3 text-left">Teléfono</th>
                <th className="px-4 py-3 text-left">Zona</th>
                <th className="px-4 py-3 text-left">Vehículo</th>
                <th className="px-4 py-3 text-left">Estado</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {teachers.map((t) => {
                const veh = vehiclesById[t.vehicle_id];
                return (
                  <tr key={t.id} className="hover:bg-neutral-50 h-[56px]" data-testid={`teacher-row-${t.id}`}>
                    <td className="px-4 py-3 font-semibold align-middle truncate" title={t.name}>{t.name}</td>
                    <td className="px-4 py-3 text-neutral-600 font-mono text-xs align-middle truncate">{t.phone || "—"}</td>
                    <td className="px-4 py-3 text-neutral-600 align-middle truncate">
                      {t.zone ? (<><MapPin className="inline h-3 w-3 mr-0.5 text-neutral-400" />{t.zone}</>) : "—"}
                    </td>
                    <td className="px-4 py-3 text-neutral-600 text-xs align-middle truncate">
                      {veh ? (
                        <span className="inline-flex items-center gap-1"><Car className="h-3 w-3 text-neutral-400" />{veh.plate} {veh.brand}</span>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3 align-middle">
                      <button
                        type="button"
                        onClick={() => toggleActive(t)}
                        data-testid={`teacher-toggle-${t.id}`}
                        className={
                          "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors " +
                          (t.active ? "bg-green-100 text-green-800 hover:bg-green-200" : "bg-neutral-200 text-neutral-700 hover:bg-neutral-300")
                        }
                      >
                        {t.active ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
                        {t.active ? "Activo" : "Inactivo"}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right align-middle">
                      <div className="inline-flex gap-1">
                        <button type="button" onClick={() => setEditing(t)} className="btn-ghost border border-neutral-200 !h-8 !px-2 text-xs" data-testid={`teacher-edit-${t.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button type="button" onClick={() => remove(t)} className="btn-ghost border border-red-200 text-red-600 hover:bg-red-50 !h-8 !px-2 text-xs" data-testid={`teacher-delete-${t.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <TeacherDialog
        open={!!editing}
        teacher={editing === "new" ? null : editing}
        vehicles={vehicles}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </div>
  );
}
